import ConfigurationService from '../services/ConfigurationService';

/**
 * Centralized registry of all API endpoints.
 * 
 * DESIGN RATIONALE:
 * - Uses polymorphic getters to ensure 100% dynamic paths.
 * - Sourced from ConfigurationService to honor environment variables.
 * - Maintains backward compatibility with existing static-like usage.
 */
export const API_ENDPOINTS = {
  get AUTH() {
    const routes = ConfigurationService.getAuthRoutes();
    return {
      LOGIN: ConfigurationService.get('VITE_API_ENDPOINT_AUTH_LOGIN') as string || '/auth/login',
      REFRESH: routes.refresh,
      ME: ConfigurationService.get('VITE_API_ENDPOINT_AUTH_ME') as string || '/auth/me',
      LOGOUT: routes.logout,
      VERIFY_PASSWORD: ConfigurationService.get('VITE_API_ENDPOINT_AUTH_VERIFY_PASSWORD') as string || '/auth/verify-password',
      CHANGE_PASSWORD: ConfigurationService.get('VITE_API_ENDPOINT_AUTH_CHANGE_PASSWORD') as string || '/auth/change-password',
      CHANGE_PASSWORD_FIRST_LOGIN: ConfigurationService.get('VITE_API_ENDPOINT_AUTH_CHANGE_PASSWORD_FIRST_LOGIN') as string || '/auth/change-password-first-login',
      FORGOT_PASSWORD: ConfigurationService.get('VITE_API_ENDPOINT_AUTH_FORGOT_PASSWORD') as string || '/auth/forgot-password',
      RESET_PASSWORD: ConfigurationService.get('VITE_API_ENDPOINT_AUTH_RESET_PASSWORD') as string || '/auth/reset-password',
      TWO_FACTOR: {
        GENERATE: ConfigurationService.get('VITE_API_ENDPOINT_AUTH_2FA_GENERATE') as string || '/auth/2fa/generate',
        VERIFY: ConfigurationService.get('VITE_API_ENDPOINT_AUTH_2FA_VERIFY') as string || '/auth/2fa/verify',
        DISABLE: ConfigurationService.get('VITE_API_ENDPOINT_AUTH_2FA_DISABLE') as string || '/auth/2fa/disable',
      },
    };
  },
  ENUMS: ConfigurationService.get('VITE_API_ENDPOINT_ENUMS') as string || '/enums',
  get PUBLIC() {
    return {
      CONFIG: ConfigurationService.get('VITE_API_ENDPOINT_PUBLIC_CONFIG') as string || '/public/config',
      FRONTEND_CONFIG: ConfigurationService.get('VITE_API_ENDPOINT_PUBLIC_FRONTEND_CONFIG') as string || '/public/frontend-config',
      LOGIN_ROLES: ConfigurationService.get('VITE_API_ENDPOINT_PUBLIC_LOGIN_ROLES') as string || '/public/login-roles',
      PAYMENT_GATEWAYS: ConfigurationService.get('VITE_API_ENDPOINT_PUBLIC_PAYMENT_GATEWAYS') as string || '/public/payment-gateways',
      FEATURE_FLAGS: '/public/feature-flags',
    };
  },
  SYSTEM: {
    CONFIGURATION: '/system/configuration',
    RATE_LIMITS: '/system/rate-limits',
    FEATURE_FLAG_UPDATE: (key: string) => `/system/feature-flags/${key}`,
    PAYMENT_GATEWAYS: {
      BASE: '/system/payment-gateways',
      BY_ID: (id: string) => `/system/payment-gateways/${id}`,
    },
  },
  ADMIN: {
    DASHBOARD_STATS: {
      USERS: '/admin/dashboard-stats/users',
      SCHOLARSHIPS: '/admin/dashboard-stats/scholarships',
      FEEDBACK: '/admin/dashboard-stats/feedback',
    },
    HEALTH: {
      HISTORY: '/admin/health/history',
    },
    COMMUNICATION_RULES: {
      BASE: '/admin/communication-rules',
      BY_ID: (id: string) => `/admin/communication-rules/${id}`,
      RESTORE: (id: string) => `/admin/communication-rules/${id}/restore`,
    },
    CONFIGURATION: {
      FRONTEND: '/admin/configuration/frontend',
    },
    AUDIT: {
      LOGS: '/admin/audit/logs',
    },
    SECURITY: {
      ANOMALIES: '/admin/security/anomalies',
      ANOMALY_STATUS: (id: string) => `/admin/security/anomalies/${id}/status`,
      ANOMALY_DETAILS: (id: string) => `/admin/security/anomalies/${id}/details`,
      ANOMALY_ACCESS_HISTORY: (id: string) => `/admin/security/anomalies/${id}/access-history`,
      SUMMARY: '/admin/security/analytics/summary',
      TRENDS: '/admin/security/analytics/trends',
    },
    WORKFLOWS: {
      BASE: '/admin/workflows',
      INSTANCES: '/admin/workflows/instances',
      STATS: '/admin/workflows/stats',
      INSTANCE_DETAIL: (id: string) => `/admin/workflows/instances/${id}`,
      RETRY: (id: string) => `/admin/workflows/instances/${id}/retry`,
      CANCEL: (id: string) => `/admin/workflows/instances/${id}/cancel`,
      FORENSICS: {
        BASE: '/admin/workflows/forensics',
        AUDIT_LOGS: '/admin/workflows/forensics/audit-logs',
        REPLAY: (auditLogId: string) => `/admin/workflows/forensics/replay/${auditLogId}`,
        METRICS: '/admin/observability/workflow-metrics',
      },
    },
    GOVERNANCE: {
      BASE: '/admin/governance',
      POLICIES: '/admin/governance/policies',
      VIOLATIONS: '/admin/governance/violations',
      DLQ: {
        BASE: '/admin/governance/dlq',
        BY_NAME: (queueName: string) => `/admin/governance/dlq/${queueName}`,
        REPLAY: (queueName: string) => `/admin/governance/dlq/${queueName}/replay`,
        PURGE: (queueName: string) => `/admin/governance/dlq/${queueName}/purge`,
      },
      QUEUES: {
        SUMMARIES: '/admin/governance/queues/summaries',
        PURGE: (prefix: string, tier: string) => `/admin/governance/queues/${prefix}/tiers/${tier}/purge`,
        REPLAY_RETRY: (prefix: string) => `/admin/governance/queues/${prefix}/retry/replay`,
        SUSPEND: (prefix: string) => `/admin/governance/queues/${prefix}/suspend`,
        RESUME: (prefix: string) => `/admin/governance/queues/${prefix}/resume`,
        QUARANTINE: (prefix: string) => `/admin/governance/queues/${prefix}/quarantine`,
        CONSUMER_STATUSES: '/admin/governance/queues/consumers/statuses',
      },
    },
    FINANCIALS: {
      ANALYTICS: {
        OVERVIEW: '/admin/financials/analytics/overview',
        REVENUE_FORECAST: '/admin/financials/analytics/forecast/revenue',
        CASH_FLOW_PREDICTION: '/admin/financials/analytics/forecast/cash-flow',
      },
      RECONCILIATION: {
        BASE: '/admin/financials/reconciliation',
        UPLOAD: '/admin/financials/reconciliation/upload',
        REPORTS: '/admin/financials/reconciliation/reports',
      },
    },
    NAVIGATION_LINKS: {
      BASE: '/admin/navigation-links',
      BY_ROLE: (role: string) => `/admin/navigation-links/role/${role}`,
      GROUPED: '/admin/navigation-links/grouped',
      BY_ID: (id: string) => `/admin/navigation-links/${id}`,
      REORDER: '/admin/navigation-links/reorder',
      ROLES: '/admin/navigation-links/roles',
      STATISTICS: '/admin/navigation-links/statistics',
    },
    NOTIFICATIONS: {
      OUTBOX: '/admin/notifications/outbox',
      OUTBOX_RETRY: (id: string) => `/admin/notifications/outbox/${id}/retry`,
      DELIVERY_LOGS: '/admin/notifications/delivery-logs',
      DELIVERY_LOG_RESUBMIT: (id: string) => `/admin/notifications/delivery-logs/${id}/resubmit`,
      CIRCUIT_BREAKER_STATES: '/admin/notifications/circuit-breaker/states',
      CIRCUIT_BREAKER_TOGGLE: '/admin/notifications/circuit-breaker/toggle',
      ANALYTICS: '/admin/notifications/analytics',
    },
    OBSERVABILITY: {
      SYSTEM_METRICS: '/admin/observability/metrics',
      COMM_METRICS: '/admin/observability/communication-metrics',
    },
    JIT: {
      BASE: '/admin/jit',
      ACTIVE: '/admin/jit/active',
      GRANT: '/admin/jit/grant',
      REVOKE: (id: string) => `/admin/jit/revoke/${id}`,
      PERMISSIONS: '/admin/permissions',
    },
    ROLES: {
      BASE: '/admin/roles',
      BY_ID: (id: string) => `/admin/roles/${id}`,
      PERMISSIONS: '/admin/permissions',
      ROLE_PERMISSION: (roleId: string, permissionId: string) => `/admin/roles/${roleId}/permissions/${permissionId}`,
    },
    FEEDBACK: {
      BASE: '/admin/feedback',
      BY_ID: (id: string) => `/admin/feedback/${id}`,
      REPLIES: (id: string) => `/admin/feedback/${id}/replies`,
      ASSIGN_STAFF: (id: string) => `/admin/feedback/${id}/assign-staff`,
    },
  },
  ROLES: {
    BASE: '/roles',
    PERMISSIONS: '/roles/permissions',
    BY_ID: (id: string) => `/roles/${id}`,
    UPDATE_PERMISSIONS: (id: string) => `/roles/${id}/permissions`,
  },
  FINANCIALS: {
    COST: {
      BASE: '/financials/cost',
      BUDGETS: '/financials/cost/budgets',
      EXPENSES: '/financials/cost/expenses',
    },
  },
  REPORTS: {
    GET_DATA: (reportType: string) => `/reports/${reportType}`,
    GENERATE: (reportType: string) => `/reports/${reportType}`,
  },
  LOGS: {
    MY_ACTIVITY: '/logs/me',
  },
  IDENTITY: {
    GENERATE_USERNAME: '/identity/generate-username',
  },
  USERS: {
    BASE: '/users',
    STATS: '/users/stats',
    BY_ID: (id: string) => `/users/${id}`,
    STATUS: (id: string) => `/users/${id}/status`,
    RESET_PASSWORD: (id: string) => `/users/${id}/reset-password`,
    RESEND_WELCOME: (id: string) => `/users/${id}/resend-welcome`,
    BULK_OPERATION_STATUS: (id: string) => `/users/bulk-uploads/${id}`,
    BULK_CREATE: '/users/bulk-create',
    BULK: {
      STATUS: '/users/bulk/status',
      RESET_PASSWORD: '/users/bulk/reset-password',
      DELETE: '/users/bulk/delete',
    },
    ME: {
      EXPORT: '/users/me/export',
      ACCOUNT: '/users/me/account',
      PROFILE: '/users/me/profile',
      NOTIFICATION_PREFERENCES: '/users/me/notification-preferences',
      PREFERENCES: '/users/me/preferences',
    },
  },
  STUDENT: {
    DASHBOARD: '/student/dashboard',
    PAYMENT_DUES: '/student/payment-dues',
    PAYMENT_HISTORY: '/student/payment-history',
    INITIATE_PAYMENT: '/student/initiate-payment',
    INSTALLMENT_SCHEDULE: '/student/installment-schedule',
    RECEIPTS: '/student/receipts',
    NOTIFICATION_PREFERENCES: '/student/me/notification-preferences',
    SELF_ENROLLMENT_PLANS: '/student/self-enrollment-plans',
    MY_GRANTS: '/student/my-grants',
    MY_GRANT_DETAILS: (id: string) => `/student/my-grants/${id}`,
  },
  STAFF: {
    FEE_STRUCTURES: {
      BASE: '/staff/fee-structures',
      BY_ID: (id: string) => `/staff/fee-structures/${id}`,
    },
    INSTALLMENT_TEMPLATES: '/staff/installment-templates',
    ASSIGNED_INSTALLMENT_PLANS: '/staff/assigned-installment-plans',
    ELIGIBLE_STUDENTS: '/staff/installment-plans/find-eligible-students',
    INSTALLMENT_PLANS: {
      BATCH_ASSIGN: '/staff/installment-plans/assign-to-students',
    },
    FEES: {
      BASE: '/fees',
      BY_ID: (id: string) => `/fees/${id}`,
    },
    FEEDBACK: {
      BASE: '/staff/feedback',
      BY_ID: (id: string) => `/staff/feedback/${id}`,
    },
  },
  PAYMENTS: {
    INITIATE: '/payments/initiate',
    VERIFY_SESSION: (id: string) => `/payments/verify-session/${id}`,
    FINALIZE: (transactionId: string) => `/payments/finalize/${transactionId}`,
    RECEIPT_DATA: (paymentId: string) => `/payments/receipt/${paymentId}`,
    DOWNLOAD_TOKEN: (paymentId: string) => `/payments/receipt/${paymentId}/download-token`,
    DOWNLOAD_PDF: (paymentId: string) => `/payments/receipt/${paymentId}/pdf`,
  },
  SCHOLARSHIPS: {
    STATS: '/scholarships/stats',
    APPLICATIONS: {
      BASE: '/scholarships/applications',
      BULK: '/scholarships/applications/bulk',
      BY_ID: (id: string) => `/scholarships/applications/${id}`,
      ACTIVATE: (id: string) => `/scholarships/applications/${id}/activate`,
      REVIEW: (id: string) => `/scholarships/applications/${id}/review`,
    },
  },
  NOTIFICATIONS: {
    GET_ALL: '/notifications',
    MARK_READ: (id: string) => `/notifications/${id}/read`,
    MARK_ALL_READ: '/notifications/read-all',
    DELETE: (id: string) => `/notifications/${id}`,
  },
  ACADEMIC: {
    STANDING: '/student/academic-standing',
    PROMOTIONS: {
      SESSIONS: '/academic/promotions/sessions',
      SESSION_BY_ID: (id: string) => `/academic/promotions/sessions/${id}`,
      OPEN: (id: string) => `/academic/promotions/sessions/${id}/open`,
      CLOSE: (id: string) => `/academic/promotions/sessions/${id}/close`,
      RETRY: (id: string) => `/academic/promotions/sessions/${id}/retry`,
      RESULTS: (id: string) => `/academic/promotions/sessions/${id}/results`,
      STATS: (id: string) => `/academic/promotions/sessions/${id}/stats`,
      PREVIEW: (id: string) => `/academic/promotions/sessions/${id}/preview`,
      RECONCILE: (id: string) => `/academic/promotions/sessions/${id}/reconcile`,
      OVERRIDE: '/academic/promotions/sessions/override',
      PROPOSE_OVERRIDE: '/academic/promotions/sessions/propose-override',
      APPROVE_OVERRIDE: '/academic/promotions/sessions/approve-override',
      REJECT_OVERRIDE: '/academic/promotions/sessions/reject-override',
      EXPORT: (id: string) => `/academic/promotions/sessions/${id}/results/export`,
      EXCEPTIONS: (id: string) => `/academic/promotions/sessions/${id}/exceptions`,
      DELETE: (id: string) => `/academic/promotions/sessions/${id}`,
      RESTORE: (id: string) => `/academic/promotions/sessions/${id}/restore`,
      ELIGIBLE_CHECKERS: '/academic/promotions/sessions/eligible-checkers',
      GET_BREAK_GLASS_STATE: '/academic/promotions/break-glass/state',
      ACTIVATE_BREAK_GLASS: '/academic/promotions/break-glass/activate',
      DEACTIVATE_BREAK_GLASS: (id: string) => `/academic/promotions/break-glass/${id}/deactivate`,
    },
    RECORDS: {
      IMPORTS: '/academic/records/imports',
      IMPORT_BY_ID: (id: string) => `/academic/records/imports/${id}`,
      VALIDATE: (id: string) => `/academic/records/imports/${id}/validate`,
      CONFIRM: (id: string) => `/academic/records/imports/${id}/confirm`,
      CANCEL: (id: string) => `/academic/records/imports/${id}/cancel`,
      UNDO: (id: string) => `/academic/records/imports/${id}/undo`,
      ROSTER: '/academic/records/roster',
      BATCH: '/academic/records/batch',
    },
    ELIGIBILITY: {
      CONFIG: '/admin/promotion-config',
    },
  },
  UI: {
    NAVIGATION: '/ui/navigation',
  },
} as const;

